import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    input,
    Signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';

/**
 * directives
 */
import { BadgeDirective } from '@shared/directives';
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';

/**
 * Displays a translated fallback label (e.g. "N/A") when `value` matches one of the `triggers`.
 * Consumers can override the rendered content via a named `valueTemplate` slot.
 */
@Component({
    selector: 'app-fallback',
    templateUrl: './fallback.component.html',
    styleUrl: './fallback.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, TranslatePipe, BadgeDirective],
})
export class FallbackComponent {

    /** The value to display; when it matches a trigger the fallback label is shown instead. */
    public readonly value = input<any>(null);

    /** Values that activate the fallback label. Defaults to `[null, undefined, '']`. */
    public readonly triggers = input<any[]>([null, undefined, '']);

    /** Translation key rendered when the fallback is active. Defaults to `'GENERAL.NA'`. */
    public readonly fallbackKey = input<string>('GENERAL.NA');

    /** All `TemplateDirective` instances projected into the component's content. */
    protected readonly templateDirectives = contentChildren(TemplateDirective);

    /** True when `value` matches one of the `triggers`; drives the fallback / value branch. */
    protected readonly isTriggered: Signal<boolean> = computed(() =>
        this.triggers().includes(this.value())
    );

    /**
     * @description Finds a named template slot among projected `TemplateDirective` instances.
     * @param tempName - Slot name (e.g. `header`, `node`, `empty`).
     * @returns The directive instance or `undefined`.
     */
    protected getTemplate(tempName: string): TemplateDirective | undefined {
        const list = this.templateDirectives();
        return list.find((d) => d.name === tempName);
    }

}
